import React, { useMemo } from 'react';
import { WorkplaceMembersView } from '../../Enums/WorkplaceMembersView.enum';
import EArchiveGroupMembersList from './EArchiveGroupMembersList/EArchiveGroupMembersList';
import EArchiveGroupMembersActions from './EArchiveGroupActions/EArchiveGroupMembersActions/EArchiveGroupMembersActions';
import EArchiveGroupSettingsActions from './EArchiveGroupActions/EArchiveGroupSettingsActions/EArchiveGroupSettingsActions';
import { useGroupQuery } from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import Text from '../../../../Components/Base/Text/Text';
import styles from './EArchiveGroup.module.scss';
import { GroupDetailListContent } from '../../Enums/GroupDetailListContent.enum';
import { useSelectUsersContext } from '../../Services/SelectUsersContext';
import EArchivePatientsListWrapper from '../EArchivePatientsListWrapper/EArchivePatientsListWrapper';
import { useGroupsContext } from '../../Services/GroupsContext';
import EArchiveAddMembersToGroupToolbar from '../EArchiveGroupToolbar/EArchiveAddMembersToGroupToolbar';
import EArchiveRemoveMembersFromGroupToolbar from '../EArchiveGroupToolbar/EArchiveRemoveMembersFromGroupToolbar';
import EArchiveGroupCentrumActions from './EArchiveGroupActions/EArchiveGroupCentrumActions/EArchiveGroupCentrumActions';

interface Props {
	view: WorkplaceMembersView;
	groupId: string;
	isSystemGroup: boolean;
	groupType?: string;
	onDelete: () => void;
}

const EArchiveGroup = ({
	view,
	groupId,
	isSystemGroup,
	groupType,
	onDelete,
}: Props) => {
	const { data, isLoading, refetch } = useGroupQuery({ id: groupId });
	const { startSelectingUsers } = useSelectUsersContext();
	const { groupDetailListContent, setGroupDetailListContent } =
		useGroupsContext();

	const onAddMembers = () => {
		setGroupDetailListContent(GroupDetailListContent.MEMBERS_ADD);
		startSelectingUsers();
	};

	const onRemoveMembers = () => {
		setGroupDetailListContent(GroupDetailListContent.MEMBERS_REMOVE);
		startSelectingUsers();
	};

	const actionsComponent = useMemo(() => {
		if (isSystemGroup) {
			return <></>;
		}

		switch (view) {
			case WorkplaceMembersView.MEMBERS:
				return (
					<EArchiveGroupMembersActions
						onAddMembers={onAddMembers}
						onRemoveMembers={onRemoveMembers}
					/>
				);
			case WorkplaceMembersView.SETTINGS:
				return (
					<EArchiveGroupSettingsActions
						groupId={groupId}
						onUpdate={refetch}
						onDelete={onDelete}
					/>
				);
			case WorkplaceMembersView.ACTIONS:
				return <EArchiveGroupCentrumActions groupId={groupId} />;
			default:
				return <></>;
		}
	}, [view, isSystemGroup]);

	const toolbarComponent = useMemo(() => {
		switch (groupDetailListContent) {
			case GroupDetailListContent.MEMBERS_ADD:
				return <EArchiveAddMembersToGroupToolbar groupId={groupId} />;
			case GroupDetailListContent.MEMBERS_REMOVE:
				return <EArchiveRemoveMembersFromGroupToolbar groupId={groupId} />;
			case GroupDetailListContent.MEMBERS:
			default:
				return <></>;
		}
	}, [groupDetailListContent]);

	const listComponent = useMemo(() => {
		switch (groupDetailListContent) {
			case GroupDetailListContent.MEMBERS:
			case GroupDetailListContent.MEMBERS_REMOVE:
				return (
					<EArchiveGroupMembersList
						key={groupDetailListContent}
						groupId={groupId}
						groupType={groupType}
					/>
				);
			case GroupDetailListContent.MEMBERS_ADD:
				return <EArchivePatientsListWrapper />;
		}
	}, [groupDetailListContent, groupId, groupType]);

	if (isLoading) {
		return <Loading />;
	}

	const title = data?.group?.name || 'base.group';

	return (
		<>
			{actionsComponent}
			{toolbarComponent}
			<div className={styles.wrapper}>
				<Text firstUpper={true} tx={title} className={styles.title} />
				{listComponent}
			</div>
		</>
	);
};

export default EArchiveGroup;
